import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";

import { graphqlClient } from "@/api/graphql";
import { cropUnitSchema } from "@/api/types/digitalnisklady.cz/types";
import { graphql } from "@/generated/digitalnisklady.cz";
import { Currency } from "@/generated/digitalnisklady.cz/graphql";

// eslint-disable-next-line lingui/no-unlocalized-strings
const offerQueryDocument = graphql(`
  query Offer($id: ID!, $locale: String) {
    offer(id: $id) {
      yearHarvested
      crop {
        id
        name(locale: $locale)
        unit
      }
      id
      amount
      totalAmount
      storage {
        id
        label
        address
        company {
          id
          name
        }
      }
    }
  }
`);

// eslint-disable-next-line lingui/no-unlocalized-strings
const offerPricesQueryDocument = graphql(`
  query OfferPrices($id: ID!, $currency: Currency!, $cropIds: [ID!]) {
    offer(id: $id) {
      storage {
        lastPrices(cropIds: $cropIds) {
          edges {
            node {
              id
              price(currency: $currency)
              quarter {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`);

const offerSchema = z.object({
  crop: z.object({
    id: z.string(),
    name: z.string(),
    unit: cropUnitSchema,
  }),
  harvestYear: z.number(),
  id: z.string(),
  price: z.object({
    perUnit: z.number().or(z.undefined()),
    total: z.number().or(z.undefined()),
  }),
  storage: z.object({
    address: z.string(),
    company: z.object({
      name: z.string(),
    }),
    id: z.string(),
    name: z.string(),
    offer: z.object({
      amount: z.number(),
      totalAmount: z.number(),
    }),
  }),
});

type Offer = z.infer<typeof offerSchema>;

const offerPricesQuery = ({
  id,
  currency,
  cropId,
}: {
  id: string;
  currency: "CZK" | "EUR";
  cropId: string;
}) =>
  queryOptions({
    queryFn: () =>
      graphqlClient.request(offerPricesQueryDocument, {
        cropIds: [cropId],
        currency: z.enum([Currency.Czk, Currency.Eur]).parse(currency),
        id,
      }),
    queryKey: ["offer", id, currency, cropId],
    select: (data) => {
      const prices = data.offer?.storage?.lastPrices?.edges?.map((edge) => ({
        currency,
        id: edge?.node?.id,
        quarter: edge?.node?.quarter?.name,
        value: edge?.node?.price,
      }));

      const schema = z.array(
        z.object({
          currency: z.string(),
          id: z.string(),
          quarter: z.string(),
          value: z.number(),
        }),
      );

      return schema.parse(prices);
    },
  });

const offerQuery = ({ id, locale }: { id: string; locale: string }) =>
  queryOptions({
    queryFn: () =>
      graphqlClient.request(offerQueryDocument, {
        id,
        locale,
      }),
    queryKey: ["offer", id, locale],
    select: (data) => {
      const result = {
        crop: {
          id: data.offer?.crop?.id,
          name: data.offer?.crop?.name,
          unit: data.offer?.crop?.unit,
        },
        harvestYear: data.offer?.yearHarvested,
        id: data.offer?.id,
        price: {
          perUnit: 0,
          total: 0,
        },
        storage: {
          address: data.offer?.storage?.address,
          company: {
            name: data.offer?.storage?.company?.name,
          },
          id: data.offer?.storage?.id,
          name: data.offer?.storage?.label,
          offer: {
            amount: data.offer?.amount,
            harvestYear: data.offer?.yearHarvested,
            totalAmount: data.offer?.totalAmount,
          },
        },
      };

      return offerSchema.parse(result);
    },
  });

export { offerPricesQuery, offerQuery };
export type { Offer };
